/**
 * Where the hours went, where they did not, and what has not been billed.
 *
 * Three things make this more than a list of entries:
 *
 *  - **By client**, because "which clients eat our time" is the question an
 *    MSP owner actually asks, and the billable/written-off split is where an
 *    unprofitable one becomes visible.
 *  - **Worked but never logged**, with a Log it button. A list of problems you
 *    cannot act on is a report; one you can clear down changes behaviour.
 *  - **Unbilled billable**, straight from ConnectWise's invoiceFlag — money
 *    sitting on the table, per client.
 *
 * Deliberately absent: a utilisation percentage. It needs dailyCapacity to be
 * right, and on this install that reads 8 for one member, 24 for another and
 * nothing for two more. A confident wrong number is worse than none.
 *
 * Bars are divs, following AROverview.jsx — the project has no charting
 * dependency and this is not the place to add the first one.
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock, AlertTriangle, Play, RefreshCw, Loader2, Receipt, Users, X, Download } from 'lucide-react';
import { adminTimeAPI } from '../../services/api';
import AdminTimeEntryModal from '../../components/admin/AdminTimeEntryModal';

const RANGES = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const BILLING = [
  { value: 'Billable', label: 'Billable' },
  { value: 'DoNotBill', label: 'Do not bill' },
  { value: 'NoCharge', label: 'No charge' },
  { value: 'NoDefault', label: 'No default' },
];

/** Which query params are filters, so one place decides what goes in the URL. */
const FILTER_KEYS = [
  'days',
  'from',
  'to',
  'person',
  'companyId',
  'billableOption',
  'source',
  'workType',
  'board',
  'search',
  'page',
];

const hrs = (n) => `${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString()}h`;

function Bar({ value, max, className = 'bg-primary-500' }) {
  const pct = max > 0 ? Math.max(1, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2 w-full rounded-full bg-neutral-100 dark:bg-neutral-700">
      <div className={`h-2 rounded-full ${className}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Billable and written-off as one bar, because the ratio is the point. */
function SplitBar({ billable, writtenOff, max }) {
  const pct = (v) => (max > 0 ? Math.max(v > 0 ? 1 : 0, Math.round((v / max) * 100)) : 0);
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-700">
      <div className="h-2 bg-green-500" style={{ width: `${pct(billable)}%` }} title={`${hrs(billable)} billable`} />
      <div className="h-2 bg-amber-500" style={{ width: `${pct(writtenOff)}%` }} title={`${hrs(writtenOff)} written off`} />
    </div>
  );
}

/**
 * Where a time entry's charge target actually lives.
 *
 * A ProjectTicket is not a service ticket: ConnectWise serves it from
 * /project/tickets and 404s on /service/tickets, so linking it to the portal's
 * ticket page guarantees a "not found". Project work links to its project
 * instead, which is a page that can actually show it.
 */
function TicketCell({ entry }) {
  if (!entry.ticketId) return <span className="text-neutral-400">—</span>;
  if (entry.chargeToType === 'ProjectTicket') {
    return (
      <div>
        {entry.projectId ? (
          <Link to={`/projects/${entry.projectId}`} className="text-primary-600 hover:underline dark:text-primary-400">
            #{entry.ticketId}
          </Link>
        ) : (
          <span className="text-neutral-700 dark:text-neutral-300">#{entry.ticketId}</span>
        )}
        <span
          className="ml-1 rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300"
          title={[entry.projectName, entry.phase].filter(Boolean).join(' · ') || 'Project work'}
        >
          project
        </span>
      </div>
    );
  }
  return (
    <Link to={`/tickets/${entry.ticketId}?from=admin`} className="text-primary-600 hover:underline dark:text-primary-400">
      #{entry.ticketId}
    </Link>
  );
}

/**
 * How promptly somebody writes their time down.
 *
 * This, not the hours total, is the number worth watching when the problem is
 * people saving it all up for the end of the month. Time reconstructed three
 * weeks later is guesswork, and it delays the invoice by the same three weeks.
 *
 * Measured here before it was shown: one technician 94% same-day, another 4%
 * with a 15.85-day median.
 */
function Promptness({ person }) {
  if (person.sameDayRate === null || person.sameDayRate === undefined) return null;
  const good = person.sameDayRate >= 80;
  const poor = person.sameDayRate < 50;
  return (
    <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
      <span className={good ? 'text-green-700 dark:text-green-400' : poor ? 'text-amber-600 dark:text-amber-400' : ''}>
        {person.sameDayRate}% logged same day
      </span>
      {person.medianDaysLate > 0 && ` · typically ${person.medianDaysLate} day${person.medianDaysLate === 1 ? '' : 's'} later`}
      {person.lateEntries > 0 && ` · ${person.lateEntries} over a week late`}
    </p>
  );
}

function Stat({ label, value, tone = 'neutral', hint = null }) {
  const tones = {
    neutral: 'text-neutral-900 dark:text-white',
    warn: 'text-amber-600 dark:text-amber-400',
    good: 'text-green-700 dark:text-green-400',
  };
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
      <p className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${tones[tone]}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>}
    </div>
  );
}

const selectClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-900 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white';

export default function AdminTimePage() {
  // The URL is the filter state, so a filtered view is shareable and survives
  // a refresh — the same approach AdminTicketsPage takes.
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchDraft, setSearchDraft] = useState(searchParams.get('search') || '');
  const [logging, setLogging] = useState(null);
  const [exporting, setExporting] = useState(false);

  const filters = Object.fromEntries(FILTER_KEYS.map((k) => [k, searchParams.get(k) || '']));
  const days = filters.days || (filters.from ? '' : '7');

  const setFilter = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      for (const [k, v] of Object.entries(patch)) {
        if (v === '' || v === null || v === undefined) next.delete(k);
        else next.set(k, String(v));
      }
      // Any change but paging returns to the first page, or you end up on
      // page 9 of a 2-page result and think it is empty.
      if (!('page' in patch)) next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      for (const k of FILTER_KEYS) if (filters[k]) params[k] = filters[k];
      if (!params.from && !params.days) params.days = 7;
      const res = await adminTimeAPI.report(params);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not load the time report.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /** Exports what is filtered, not what is on screen. */
  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const params = {};
      for (const k of FILTER_KEYS) if (filters[k] && k !== 'page') params[k] = filters[k];
      if (!params.from && !params.days) params.days = 7;
      const res = await adminTimeAPI.exportCsv(params);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `time-${data?.window?.from || 'export'}-to-${data?.window?.to || ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Could not build the export.');
    } finally {
      setExporting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data]);

  useEffect(() => {
    load();
  }, [load]);

  const activeCount = FILTER_KEYS.filter((k) => !['days', 'page'].includes(k) && filters[k]).length;
  const maxPerson = Math.max(1, ...(data?.byPerson || []).map((p) => p.hours));
  const maxClient = Math.max(1, ...(data?.byClient || []).map((c) => c.hours));
  const page = Number(filters.page) || 1;
  const pagination = data?.entries?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Time</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            ConnectWise entries and the portal's own ledger together, plus work that was never logged.
            {data?.window && (
              <span className="ml-1">
                {data.window.from} to {data.window.to} ({data.window.timezone}).
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-neutral-300 dark:border-neutral-600">
            {RANGES.map((r) => (
              <button
                key={r.days}
                type="button"
                onClick={() => setFilter({ days: r.days, from: '', to: '' })}
                className={`px-3 py-1.5 text-sm first:rounded-l-lg last:rounded-r-lg ${
                  String(days) === String(r.days) && !filters.from
                    ? 'bg-primary-600 text-white'
                    : 'text-neutral-700 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-700'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            className="flex items-center gap-2 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-700"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </button>
          <button
            type="button"
            onClick={load}
            className="rounded-lg border border-neutral-300 p-2 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-700"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">From</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilter({ from: e.target.value, days: '' })}
              className={selectClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">To</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilter({ to: e.target.value })}
              className={selectClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Person</label>
            <select value={filters.person} onChange={(e) => setFilter({ person: e.target.value })} className={selectClass}>
              <option value="">Everyone</option>
              {(data?.options?.people || []).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Client</label>
            <select value={filters.companyId} onChange={(e) => setFilter({ companyId: e.target.value })} className={selectClass}>
              <option value="">All clients</option>
              {(data?.options?.clients || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Billing</label>
            <select
              value={filters.billableOption}
              onChange={(e) => setFilter({ billableOption: e.target.value })}
              className={selectClass}
            >
              <option value="">Any</option>
              {BILLING.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Source</label>
            <select value={filters.source} onChange={(e) => setFilter({ source: e.target.value })} className={selectClass}>
              <option value="">Both</option>
              <option value="connectwise">ConnectWise</option>
              <option value="portal">Portal staff</option>
            </select>
          </div>
          {(data?.options?.workTypes || []).length > 0 && (
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Work type</label>
              <select value={filters.workType} onChange={(e) => setFilter({ workType: e.target.value })} className={selectClass}>
                <option value="">Any</option>
                {data.options.workTypes.map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </div>
          )}
          {(data?.options?.boards || []).length > 0 && (
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Board</label>
              <select value={filters.board} onChange={(e) => setFilter({ board: e.target.value })} className={selectClass}>
                <option value="">Any</option>
                {data.options.boards.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
          )}
          <div className="min-w-[200px] flex-1">
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Search</label>
            <input
              type="text"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setFilter({ search: searchDraft })}
              onBlur={() => filters.search !== searchDraft && setFilter({ search: searchDraft })}
              placeholder="Ticket, notes, client…"
              className={`${selectClass} w-full`}
            />
          </div>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={() => {
                setSearchDraft('');
                setSearchParams(new URLSearchParams(days ? { days: String(days) } : {}), { replace: true });
              }}
              className="flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-700"
            >
              <X className="h-3.5 w-3.5" /> Clear {activeCount}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center gap-2 p-8 text-neutral-500 dark:text-neutral-400">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : (
        data && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <Stat label="Hours logged" value={hrs(data.totals.hours)} hint={`${data.totals.entries} entries`} />
              <Stat label="Billable" value={hrs(data.totals.billable)} tone="good" />
              <Stat
                label="Written off"
                value={hrs(data.totals.writtenOff)}
                tone={data.totals.writtenOff > 0 ? 'warn' : 'neutral'}
                hint="Do not bill, or no charge"
              />
              <Stat
                label="Not yet invoiced"
                value={hrs(data.totals.unbilledHours)}
                tone={data.totals.unbilledHours > 0 ? 'warn' : 'good'}
                hint="Billable, still on the table"
              />
              <Stat
                label="Worked, not logged"
                value={String(data.totals.unloggedTickets)}
                tone={data.totals.unloggedTickets > 0 ? 'warn' : 'good'}
                hint="Tickets with no time against them"
              />
            </div>

            {data.running?.length > 0 && (
              <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                <h2 className="mb-3 flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                  <Play className="h-4 w-4 text-green-600 dark:text-green-400" /> Running now
                </h2>
                <ul className="space-y-1 text-sm">
                  {data.running.map((t) => (
                    <li key={`${t.ticketId}-${t.person}`} className="flex justify-between text-neutral-700 dark:text-neutral-300">
                      <Link to={`/tickets/${t.ticketId}?from=admin`} className="text-primary-600 hover:underline dark:text-primary-400">
                        #{t.ticketId}
                      </Link>
                      <span>{t.person}</span>
                      <span className="text-neutral-500 dark:text-neutral-400">
                        since {new Date(t.startedAt).toLocaleTimeString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              {/* By client — the question an owner actually asks */}
              <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                <h2 className="mb-1 flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                  <Users className="h-4 w-4" /> By client
                </h2>
                <p className="mb-3 text-xs text-neutral-500 dark:text-neutral-400">
                  <span className="inline-block h-2 w-2 rounded-full bg-green-500" /> billable{' '}
                  <span className="ml-2 inline-block h-2 w-2 rounded-full bg-amber-500" /> written off
                </p>
                {data.byClient.length === 0 ? (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Nothing in this window.</p>
                ) : (
                  <div className="space-y-3">
                    {data.byClient.map((c) => (
                      <div key={c.companyId || c.company}>
                        <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                          <button
                            type="button"
                            onClick={() => c.companyId && setFilter({ companyId: c.companyId })}
                            className="truncate text-left text-neutral-700 hover:text-primary-600 dark:text-neutral-300 dark:hover:text-primary-400"
                          >
                            {c.company}
                          </button>
                          <span className="whitespace-nowrap text-neutral-500 dark:text-neutral-400">
                            {hrs(c.hours)}
                            {c.writtenOff > 0 && <span className="text-amber-600 dark:text-amber-400"> · {hrs(c.writtenOff)} off</span>}
                          </span>
                        </div>
                        <SplitBar billable={c.billable} writtenOff={c.writtenOff} max={maxClient} />
                        <p className="mt-0.5 truncate text-xs text-neutral-400 dark:text-neutral-500">
                          {c.agreements.length ? c.agreements.join(', ') : 'no agreement'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* By person */}
              <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                <h2 className="mb-3 flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                  <Clock className="h-4 w-4" /> By person
                </h2>
                {data.byPerson.length === 0 ? (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Nothing in this window.</p>
                ) : (
                  <div className="space-y-3">
                    {data.byPerson.map((p) => (
                      <div key={p.key || p.person} className={p.hasLoggedTime ? '' : 'opacity-60'}>
                        <div className="mb-1 flex justify-between text-sm">
                          <button
                            type="button"
                            onClick={() => setFilter({ person: p.person })}
                            className="text-left text-neutral-700 hover:text-primary-600 dark:text-neutral-300 dark:hover:text-primary-400"
                          >
                            {p.person}
                          </button>
                          <span className="text-neutral-500 dark:text-neutral-400">
                            {p.hasLoggedTime ? (
                              <>
                                {hrs(p.hours)}
                                {p.portalHours > 0 && ` · ${hrs(p.portalHours)} portal`}
                              </>
                            ) : (
                              // Said in words, not implied by an empty bar. Somebody
                              // who logged nothing is the row worth noticing.
                              <span className="text-amber-600 dark:text-amber-400">nothing logged</span>
                            )}
                          </span>
                        </div>
                        <Bar value={p.hours} max={maxPerson} />
                        <Promptness person={p} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {/* Worked but never logged — fixable in place */}
              <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                <h2 className="mb-1 flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                  <AlertTriangle className="h-4 w-4 text-amber-500" /> Worked but never logged
                </h2>
                <p className="mb-3 text-xs text-neutral-500 dark:text-neutral-400">
                  Somebody wrote a note or started a timer on these, and no time was filed.
                </p>
                {data.unlogged.length === 0 ? (
                  <p className="text-sm text-green-700 dark:text-green-400">Nothing outstanding.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.unlogged.map((u) => (
                      <li key={`${u.ticketId}-${u.userId}`} className="flex items-start justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          {/* Project gaps cannot link to the ticket page: that
                              route is service-only and 404s on project ids,
                              the same reason TicketCell above links to the
                              project. Most of the hours here are project work. */}
                          {u.projectId ? (
                            <Link
                              to={`/projects/${u.projectId}`}
                              className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                            >
                              #{u.ticketId}
                            </Link>
                          ) : (
                            <Link
                              to={`/tickets/${u.ticketId}?from=admin`}
                              className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                            >
                              #{u.ticketId}
                            </Link>
                          )}{' '}
                          <span className="text-neutral-700 dark:text-neutral-300">{u.summary}</span>
                          {u.projectName && (
                            <span
                              className="ml-1 rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300"
                              title={[u.projectName, u.phase].filter(Boolean).join(' · ')}
                            >
                              project
                            </span>
                          )}
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">
                            {u.person} · {u.actions} action{u.actions === 1 ? '' : 's'} · last{' '}
                            {new Date(u.lastAt).toLocaleDateString()}
                            {u.company && ` · ${u.company}`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setLogging(u.ticketId)}
                          className="shrink-0 rounded-lg border border-neutral-300 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-700"
                        >
                          Log it
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Unbilled billable */}
              <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                <h2 className="mb-1 flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                  <Receipt className="h-4 w-4 text-amber-500" /> Not yet invoiced
                </h2>
                <p className="mb-3 text-xs text-neutral-500 dark:text-neutral-400">
                  Billable time ConnectWise has not put on an invoice.
                </p>
                {data.unbilled.length === 0 ? (
                  <p className="text-sm text-green-700 dark:text-green-400">
                    Everything billable in this window has been invoiced.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {data.unbilled.map((u) => (
                      <li key={u.companyId || u.company} className="flex justify-between text-sm">
                        <button
                          type="button"
                          onClick={() => u.companyId && setFilter({ companyId: u.companyId, billableOption: 'Billable' })}
                          className="truncate text-left text-neutral-700 hover:text-primary-600 dark:text-neutral-300 dark:hover:text-primary-400"
                        >
                          {u.company}
                        </button>
                        <span className="font-medium text-amber-600 dark:text-amber-400">{hrs(u.hours)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Entries */}
            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800">
              <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3 dark:border-neutral-700">
                <h2 className="font-medium text-neutral-900 dark:text-white">Entries</h2>
                {pagination && (
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {pagination.total === 0
                      ? 'none'
                      : `${(pagination.page - 1) * pagination.pageSize + 1}–${Math.min(
                          pagination.page * pagination.pageSize,
                          pagination.total
                        )} of ${pagination.total}`}
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200 dark:border-neutral-700">
                      {['Ticket', 'Person', 'Client', 'Hours', 'Billing', 'Agreement', 'When', 'Source'].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                    {(data.entries.items || []).map((e) => (
                      <tr key={e.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                        <td className="px-4 py-2 text-sm">
                          <TicketCell entry={e} />
                        </td>
                        <td className="px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300">
                          {e.member}
                          {e.enteredViaPortalBy && e.enteredViaPortalBy !== e.member && (
                            <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                              entered by {e.enteredViaPortalBy}
                            </span>
                          )}
                        </td>
                        <td className="max-w-[180px] truncate px-4 py-2 text-sm text-neutral-600 dark:text-neutral-400">
                          {e.company || '—'}
                        </td>
                        <td className="px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300">
                          {hrs(e.hours)}
                          {e.hoursBilled > 0 && Math.abs(e.hoursBilled - e.hours) > 0.001 && (
                            <span className="block text-xs text-amber-600 dark:text-amber-400">
                              billed {hrs(e.hoursBilled)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-neutral-600 dark:text-neutral-400">
                          {e.billableOption}
                          {e.source === 'connectwise' && e.billableOption === 'Billable' && !e.invoiced && (
                            <span className="block text-xs text-amber-600 dark:text-amber-400">not invoiced</span>
                          )}
                        </td>
                        <td className="max-w-[140px] truncate px-4 py-2 text-sm text-neutral-600 dark:text-neutral-400">
                          {e.agreement || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-neutral-600 dark:text-neutral-400">
                          {e.at ? new Date(e.at).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {e.source === 'portal' ? (
                            <span className={`rounded px-2 py-1 text-xs font-medium ${e.clientTeam ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'}`} title={e.clientTeam ? "Logged by the client's own IT technician on their co-managed queue; never billed" : undefined}>
                              {e.clientTeam ? 'Client IT' : `Portal${e.billedThroughConnectWise ? ' · billed' : ''}`}
                            </span>
                          ) : (
                            <span className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                              CW
                            </span>
                          )}
                          {e.mirrorError && (
                            <span className="block text-xs text-red-600 dark:text-red-400" title={e.mirrorError}>
                              mirror failed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(data.entries.items || []).length === 0 && (
                  <p className="px-4 py-6 text-sm text-neutral-500 dark:text-neutral-400">
                    {activeCount > 0 ? 'Nothing matches those filters.' : 'No time logged in this window.'}
                  </p>
                )}
              </div>
              {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 dark:border-neutral-700">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setFilter({ page: page - 1 })}
                    className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 disabled:opacity-40 dark:border-neutral-600 dark:text-neutral-300"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= pagination.pages}
                    onClick={() => setFilter({ page: page + 1 })}
                    className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 disabled:opacity-40 dark:border-neutral-600 dark:text-neutral-300"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )
      )}

      {/* Logging straight from the gap list, with the modal the ticket page uses. */}
      {logging && (
        <AdminTimeEntryModal
          ticketId={logging}
          onClose={() => setLogging(null)}
          onSuccess={() => {
            setLogging(null);
            load();
          }}
        />
      )}
    </div>
  );
}
